/**
 * Root component — router + layout.
 * Public routes: /login
 * Protected routes: everything else (requires authentication)
 *
 * Route-based code splitting: all major pages are lazily loaded via React.lazy().
 * Plugin pages (e.g. MaintenancePage) are loaded through the plugin registry.
 */

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { store, loadPlugin } from '@/infra/store';
import { setNavigator } from '@/infra/navigation/history';
import { PluginRegistryProvider, pluginRegistry, PluginErrorBoundary } from '@/infra/plugin-registry';
import { maintenanceManifest } from '@/features/home-maintenance/manifest';
import { todosManifest } from '@/features/todos/manifest';
import { projectsManifest } from '@/features/projects/manifest';
import { contactsManifest } from '@/features/contacts/manifest';
import { commitmentsManifest } from '@/features/commitments/manifest';
import { AppLayout } from '@/layouts/AppLayout';
import { Loading } from '@/pages/Loading';
import { NotFound } from '@/pages/NotFound';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';

// ─── Lazy-loaded route pages (code splitting) ─────────────
const Settings = React.lazy(() => import('@/pages/Settings'));
const AdminSettings = React.lazy(() => import('@/pages/AdminSettings'));
const TodosPage = React.lazy(() => import('@/features/todos/pages/TodosPage'));
const CalendarPage = React.lazy(() => import('@/features/calendar/pages/CalendarPage'));
const LoginPage = React.lazy(() => import('@/features/auth/components/LoginPage'));
const CallbackPage = React.lazy(() => import('@/features/auth/components/CallbackPage'));
const GoogleCalendarCallback = React.lazy(() => import('@/pages/GoogleCalendarCallback'));
const ContactReviewPage = React.lazy(() => import('@/features/contacts/pages/ContactReviewPage'));

// Register plugins on app init
pluginRegistry.register(todosManifest);
pluginRegistry.register(maintenanceManifest);
pluginRegistry.register(projectsManifest);
pluginRegistry.register(contactsManifest);
pluginRegistry.register(commitmentsManifest);

// Load enabled plugins' reducers and sagas into the store
for (const plugin of pluginRegistry.getEnabledPlugins()) {
  loadPlugin(
    plugin.id,
    plugin.reducer as ((state: any, action: any) => any) | undefined,
    plugin.saga as (() => Generator) | undefined
  );
}

/**
 * Renders a lazy-loaded plugin route wrapped in PluginErrorBoundary + Suspense.
 */
function PluginRoute({ pluginId, children }: { pluginId: string; children: React.ReactNode }) {
  return (
    <PluginErrorBoundary pluginId={pluginId}>
      <Suspense fallback={<Loading />}>
        {children}
      </Suspense>
    </PluginErrorBoundary>
  );
}

/**
 * NavigationBridge — installs react-router's imperative navigate into the
 * history singleton so non-component code (sagas) can navigate.
 *
 * QPL route-change dispatch is handled synchronously by
 * installHistoryBridge() in index.tsx (patches window.history.pushState
 * / replaceState), so this component no longer touches QPL.
 */
function NavigationBridge() {
  const nav = useNavigate();
  useEffect(() => {
    setNavigator((to: string) => nav(to));
  }, [nav]);
  return null;
}

function AppRoutes() {
  // Subscribe to plugins Redux state to re-render routes when plugins are toggled
  useSelector((state: any) => state.pluginsToggle?.plugins);

  // Build dynamic plugin routes from the registry
  const pluginRoutes = pluginRegistry.getEnabledPlugins().flatMap((plugin) => {
    if (!plugin.routes || plugin.routes.length === 0) return [];
    return plugin.web_routes.map((route, idx) => {
      const LazyComponent = plugin.routes![idx];
      if (!LazyComponent) return null;
      return (
        <Route
          key={`${plugin.id}-${route.path}`}
          path={route.path}
          element={
            <ProtectedRoute>
              <AppLayout>
                <PluginRoute pluginId={plugin.id}>
                  <LazyComponent />
                </PluginRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      );
    });
  });

  return (
    <Suspense fallback={<Loading />}>
      <NavigationBridge />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route path="/calendar/google/callback" element={<GoogleCalendarCallback />} />

        {/* Protected core routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TodosPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/todos"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TodosPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CalendarPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Settings />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AdminSettings />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/contacts/:id/review"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PluginErrorBoundary pluginId="contacts">
                  <Suspense fallback={<Loading />}>
                    <ContactReviewPage />
                  </Suspense>
                </PluginErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Dynamic plugin routes */}
        {pluginRoutes}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <Provider store={store}>
      <PluginRegistryProvider registry={pluginRegistry}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </PluginRegistryProvider>
    </Provider>
  );
}

export default App;
